/**
 * Staff Types
 * 
 * Type definitions for staff/collaborator management.
 * Aligned with backend DTOs from /api/v1/staff
 */

// ============================================================================
// ENUMS
// ============================================================================

export type StaffRole =
  | 'SUPER_ADMIN'
  | 'SUPPORT_LEAD'
  | 'SUPPORT_AGENT'
  | 'SALES_MANAGER'
  | 'FINANCE'
  | 'MARKETING'
  | 'COMMERCIAL_ADVISOR';

export const STAFF_ROLE_LABELS: Record<StaffRole, string> = {
  SUPER_ADMIN: 'Super Admin',
  SUPPORT_LEAD: 'Líder de Soporte',
  SUPPORT_AGENT: 'Agente de Soporte',
  SALES_MANAGER: 'Gerente de Ventas',
  FINANCE: 'Finanzas',
  MARKETING: 'Marketing',
  COMMERCIAL_ADVISOR: 'Asesor Comercial',
};

export const STAFF_ROLE_COLORS: Record<StaffRole, string> = {
  SUPER_ADMIN: 'primary',
  SUPPORT_LEAD: 'info',
  SUPPORT_AGENT: 'default',
  SALES_MANAGER: 'success',
  FINANCE: 'warning',
  MARKETING: 'error',
  COMMERCIAL_ADVISOR: 'success',
};

// ============================================================================
// DEPARTMENT & POSITION ENUMS
// ============================================================================

export type StaffDepartment =
  | 'Tecnología'
  | 'Soporte'
  | 'Ventas'
  | 'Marketing'
  | 'Finanzas'
  | 'Recursos Humanos'
  | 'Operaciones'
  | 'Legal'
  | 'Producto'
  | 'Datos'
  | 'Diseño';

export const STAFF_DEPARTMENTS: StaffDepartment[] = [
  'Tecnología',
  'Soporte',
  'Ventas',
  'Marketing',
  'Finanzas',
  'Recursos Humanos',
  'Operaciones',
  'Legal',
  'Producto',
  'Datos',
  'Diseño',
];

/**
 * Los cargos, ordenados de mayor a menor responsabilidad.
 *
 * El orden de la lista **es** la escala: quien la ve en el desplegable lee
 * una carrera, no un montón de etiquetas. Faltaba el escalón intermedio
 * —«Semi Senior»— que es justo el que suele tener más gente, y el salto de
 * Junior a Senior obligaba a colocar mal a media plantilla.
 *
 * «Especialista» va aparte a propósito: no es un peldaño de la escala sino
 * una rama técnica, y meterlo en medio haría creer que está por encima o por
 * debajo de un analista.
 */
export type StaffPosition =
  | 'Director'
  | 'Gerente'
  | 'Coordinador'
  | 'Líder de Equipo'
  | 'Analista Senior'
  | 'Analista Semi Senior'
  | 'Analista'
  | 'Analista Junior'
  | 'Practicante'
  | 'Especialista'
  | 'Asistente';

export const STAFF_POSITIONS: StaffPosition[] = [
  'Director',
  'Gerente',
  'Coordinador',
  'Líder de Equipo',
  'Analista Senior',
  'Analista Semi Senior',
  'Analista',
  'Analista Junior',
  'Practicante',
  'Especialista',
  'Asistente',
];

// ============================================================================
// USER ENTITY (nested in Staff)
// ============================================================================

export interface StaffUser {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
  status?: string;
}

/**
 * Cuenta de cliente (Customer) vinculada al Staff — habilita los
 * beneficios de empleado (bonos, puntos, descuentos) en la cuenta
 * de app del colaborador.
 */
export interface LinkedCustomerSummary {
  id: string;
  email: string | null;
  phone: string;
  emailVerified: boolean;
  user: {
    firstName: string;
    lastName: string;
    avatarUrl: string | null;
  };
  /** Identidad de creador de contenido de esa cuenta, si la tiene. */
  influencer?: {
    code: string;
    displayName: string;
    status: string;
  } | null;
}

/** Perfil de aliado comercial del colaborador, si tiene el rol. */
export interface StaffAdvisorProfileSummary {
  id: string;
  code: string;
  status: string;
  commissionPercent: number;
}

// ============================================================================
// STAFF ENTITY
// ============================================================================

export interface Staff {
  id: string;
  userId: string;
  email: string;  // Email is now on Staff, not User
  phone: string | null; // Celular corporativo en formato E.164
  countryId: string | null;
  employeeCode: string | null;
  role: StaffRole;
  department: string | null;
  position: string | null;
  hiredAt: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  user: StaffUser;
  country?: {
    id: string;
    name: string;
    code: string;
  } | null;
  /** Cuenta de consumidor vinculada (beneficios de empleado). */
  linkedCustomer?: LinkedCustomerSummary | null;
  advisorProfile?: StaffAdvisorProfileSummary | null;
  /**
   * Rastro operativo que trae el listado. Subconjunto de las once relaciones
   * que el backend valida al borrar — suficiente para decidir si ofrecer la
   * acción, nunca para autorizarla. Ver `staff-deletion.ts`.
   */
  _count?: {
    advisorAssignments?: number;
    assignedTickets?: number;
    invoicesApproved?: number;
    bankAccountsVerified?: number;
  };
}

/**
 * Resultado de búsqueda de Customer por email/phone, usado en el flow
 * de vinculación de cuenta.
 */
export interface CustomerLookupResult {
  id: string;
  email: string | null;
  phone: string;
  emailVerified: boolean;
  user: {
    firstName: string;
    lastName: string;
    avatarUrl: string | null;
    /**
     * Estado real de la cuenta (`User.status`). Sustituye al viejo
     * `Customer.isActive`, que nadie escribía nunca: una cuenta bloqueada
     * aparecía como activa.
     */
    status: string;
  };
}

// ============================================================================
// CREATE / UPDATE DTOs
// ============================================================================

export interface CreateStaffDto {
  firstName: string;   // Nombre del colaborador
  lastName: string;    // Apellido del colaborador
  email: string;       // Email corporativo del staff (login via OTP)
  phone?: string;      // Celular corporativo en formato E.164
  role: StaffRole;
  countryId?: string;
  employeeCode?: string;
  department?: string;
  position?: string;
  hiredAt?: string;
  avatarUrl?: string;  // Foto de perfil, subida antes al módulo de medios
}

export interface UpdateStaffDto {
  firstName?: string;
  lastName?: string;
  phone?: string;
  avatarUrl?: string | null;
  role?: StaffRole;
  countryId?: string | null;
  employeeCode?: string;
  department?: string;
  position?: string;
  hiredAt?: string;
  isActive?: boolean;
}

// ============================================================================
// QUERY PARAMS
// ============================================================================

export interface StaffQueryParams {
  page?: number;
  limit?: number;
  role?: StaffRole;
  countryId?: string;
  isActive?: boolean;
  search?: string;
}

// ============================================================================
// PAGINATED RESPONSE
// ============================================================================

export interface StaffPaginatedResponse {
  data: Staff[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// ============================================================================
// WORKLOAD
// ============================================================================

export interface StaffWorkload {
  assignedTickets: {
    total: number;
    byStatus: Record<string, number>;
    byPriority: Record<string, number>;
  };
}
